// Chakra Imports
import {
  Avatar,
  Badge,
  Button,
  Flex,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
// Custom Components
import { ItemContent } from "common/components/menu/ItemContent";
import { SidebarResponsive } from "common/components/sidebar/Sidebar";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
// Assets
import { MdInfoOutline, MdNotificationsNone } from "react-icons/md";
import { FaEthereum } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getApi, postApi } from "common/services/api";
import { toast } from "react-toastify";
import { ThemeEditor } from "./ThemeEditor";
import FixedPlugin from "common/components/fixedPlugin/FixedPlugin";
import { useDispatch, useSelector } from "react-redux";
import { getDataFromStorage } from "common/utils/storage";
import { constant } from "../../constant";
import { saveDataToStorage } from "common/utils/storage";
import notificationIcon from "common/assets/img/notificationIcon.png";
import userIcon from "common/assets/img/userIcon.png";
import { useTranslation } from "react-i18next";
import { SSORole } from "common/contexts/SSOContext";
import LogoutModal from "../logoutModal";
import { roleFormatter } from "common/utils/CalcFunctions";
import { capitalizeFirstLetter } from "common/utils/textFormatters";
import { formatRoleName } from "common/utils/textFormatters";
import { multipleRoleFormatter } from "common/utils/CalcFunctions";
import { setUser } from "common/redux/reducers/localSlice";

const getPostSwitchRoute = (user) => {
  const activeRoleName =
    user?.activeRoleId?.roleName ||
    user?.roles?.find((role) => {
      const roleId = role?._id || role;
      const activeRoleId = user?.activeRoleId?._id || user?.activeRoleId;
      return String(roleId) === String(activeRoleId);
    })?.roleName ||
    "";

  return String(activeRoleName).toLowerCase() === "reviewer"
    ? "/recruitment/document-verification"
    : "/dashboard";
};

const resolveActiveRoleName = (user, fallbackActiveRoleId = null) => {
  if (!user) return "";

  if (user?.role === "superAdmin") {
    return constant.superAdminLabel;
  }

  const activeRoleId =
    fallbackActiveRoleId ||
    user?.activeRoleId?._id ||
    user?.activeRoleId;

  if (activeRoleId && Array.isArray(user?.roles)) {
    const activeRole = user.roles.find((role) => {
      const roleId = role?._id || role;
      return String(roleId) === String(activeRoleId);
    });

    if (activeRole?.roleName) {
      return formatRoleName(activeRole.roleName);
    }
  }

  if (user?.activeRoleId?.roleName) {
    return formatRoleName(user.activeRoleId.roleName);
  }

  // Only fall back to the first role for truly legacy users with no activeRoleId at all.
  if (!activeRoleId && user?.roles?.[0]?.roleName) {
    return formatRoleName(user.roles[0].roleName);
  }

  return "";
};

export default function HeaderLinks(props) {
  const {
    secondary,
    setOpenSidebar,
    openSidebar,
    openSubBar,
    isUserLayout,
    setOpenSubBar,
    routes,
  } = props;
  // Chakra Color Mode
  const navbarIcon = useColorModeValue("gray.400", "white");
  let menuBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorBrand = useColorModeValue("brand.700", "brand.400");
  const ethColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");
  const ethBg = useColorModeValue("secondaryGray.300", "navy.900");
  const ethBox = useColorModeValue("white", "navy.800");
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.18)",
    "14px 17px 40px 4px rgba(112, 144, 176, 0.06)"
  );
  // const borderButton = useColorModeValue('secondaryGray.500', 'whiteAlpha.200');
  const { t } = useTranslation();
  const [loginUser, setLoginUser] = useState();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.user);

  // Get user data from localStorage to ensure it's up to date (especially after role switch)
  const getUserDataFromStorage = () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      return (
        storedUser ||
        (typeof userData === "string" ? JSON.parse(userData) : userData)
      );
    } catch (error) {
      return typeof userData === "string" ? JSON.parse(userData) : userData;
    }
  };

  // Use localStorage user data as primary source (updated after role switch)
  const storedUserData = getUserDataFromStorage();
  const data =
    storedUserData ||
    (typeof userData === "string" ? JSON.parse(userData) : userData);
  const currentUserData = loginUser || data;
  const user = currentUserData?.firstName + " " + currentUserData?.lastName;
  const userId = currentUserData?._id || JSON.parse(localStorage.getItem("user"))?._id;
  const [isOpen, setIsOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isSwitchingRole, setIsSwitchingRole] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [currentActiveRole, setCurrentActiveRole] = useState(null);

  const fetchData = async () => {
    let response = await getApi("api/user/view/", userId);
    const userDetails = response?.data || {};
    localStorage.setItem(
      "isGoogleAccount",
      JSON.stringify(userDetails.isGoogleAccount)
    );
    localStorage.setItem(
      "isGoogleAccountSynced",
      JSON.stringify(userDetails.isGoogleAccountSynced)
    );
    localStorage.setItem(
      "isOutlookAccount",
      JSON.stringify(userDetails.isOutlookAccount)
    );
    localStorage.setItem(
      "isOutlookAccountSynced",
      JSON.stringify(userDetails.isOutlookAccountSynced)
    );
    setLoginUser(userDetails);
  };

  useEffect(() => {
    fetchData();
    fetchUserRoles();
  }, []);

  const fetchUserRoles = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData && userData._id) {
        const response = await getApi(`api/user/view/${userData._id}`);
        if (response && response.status === 200) {
          const userDataResponse = response.data;
          const roles = userDataResponse.roles || [];
          const linkedRoleIds = (userDataResponse.linkedRoleIds || []).map(lr => lr._id || lr);
          
          const defaultRole = roles.find((role) => {
            const roleId = (role._id || role).toString();
            return !linkedRoleIds.some(lrId => lrId.toString() === roleId);
          });

          const currentActiveRoleId =
            userDataResponse.activeRoleId?._id || userDataResponse.activeRoleId;

          setCurrentActiveRole(
            currentActiveRoleId || defaultRole?._id || defaultRole
          );

          const rolesToShow = [];
          if (defaultRole) {
            rolesToShow.push({
              id: defaultRole._id || defaultRole,
              name: defaultRole.roleName,
            });
          }
          
          linkedRoleIds.forEach(linkedRoleId => {
            const linkedRole = roles.find((role) => {
              const roleId = (role._id || role).toString();
              return roleId === linkedRoleId.toString();
            });
            if (linkedRole) {
              rolesToShow.push({
                id: linkedRole._id || linkedRole,
                name: linkedRole.roleName,
              });
            }
          });
          
          setUserRoles(rolesToShow.length >= 2 ? rolesToShow : []);
        }
      }
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  const handleSwitchRole = async (roleId) => {
    if (
      !roleId ||
      roleId === currentActiveRole?._id ||
      roleId === currentActiveRole
    ) {
      return;
    }

    try {
      setIsSwitchingRole(true);
      const response = await postApi("api/user/switch-role", {
        roleId: roleId,
      });

      if (response && response.status === 200) {
        if (response.data.token) {
          localStorage.setItem("token", response.data.token);
        }

        const updatedUser = response.data.user || response.data;
        if (updatedUser) {
          localStorage.setItem("user", JSON.stringify(updatedUser));
          dispatch(setUser(updatedUser));
          setLoginUser(updatedUser);
          // Set the active role ID (can be object or ID)
          const newActiveRoleId =
            updatedUser.activeRoleId?._id ||
            updatedUser.activeRoleId ||
            response.data.activeRole?._id ||
            response.data.activeRole;
          setCurrentActiveRole(newActiveRoleId);
          // Refresh user roles to update the menu
          fetchUserRoles();
        }

        const roleName =
          response.data.activeRole?.roleName ||
          updatedUser?.activeRoleId?.roleName ||
          "role";

        toast.success(
          `Switched to ${multipleRoleFormatter(roleName)} successfully`
        );

        // Clear pagination state to reset to page 1 for the new role
        localStorage.removeItem("paginationState");

        const targetPath = getPostSwitchRoute(updatedUser);
        window.location.replace(`${window.location.origin}/#${targetPath}`);
      } else {
        toast.error(
          response?.response?.data?.message || "Failed to switch role"
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to switch role");
    } finally {
      setIsSwitchingRole(false);
    }
  };
  const { intervalId } = SSORole();
  const location = useLocation();
  useEffect(() => {
    document.body.classList.remove("openFilter");
  }, [location]);

  const [isLogoutScheduled, setIsLogoutScheduled] = useState(false);

  const logOut = (message) => {
    const rememberedUser = localStorage.getItem("rememberedUser");
    localStorage.clear();
    sessionStorage.clear();
    rememberedUser && localStorage.setItem("rememberedUser", rememberedUser);
    clearInterval(intervalId);
    navigate("/auth");
    if (message) {
      toast.error(message);
    } else {
      toast.success("Log out Successfully");
    }
    setIsLogoutScheduled(true);
  };

  const roleName = loginUser?.roles?.find(
    (d) => d.roleName === "hiringManager"
  )?.roleName;
  const displayRoleName = resolveActiveRoleName(
    currentUserData,
    currentActiveRole?._id || currentActiveRole
  );

  return (
    <Flex
      className="topMenu-right"
      w={{ base: "100%", md: "auto" }}
      alignItems="center"
      justifyContent={"end"}
      flexDirection="row"
      bg={menuBg}
      flexWrap="nowrap"
      flexShrink={0}
    >
      <Flex
        bg={ethBg}
        display={secondary ? "flex" : "none"}
        borderRadius="30px"
        ms="auto"
        p="6px"
        align="center"
        me="6px"
      >
        <Flex
          align="center"
          justify="center"
          bg={ethBox}
          h="29px"
          w="29px"
          borderRadius="30px"
          me="7px"
        >
          <Icon color={ethColor} w="9px" h="14px" as={FaEthereum} />
        </Flex>
        <Text
          w="max-content"
          color={ethColor}
          fontSize="sm"
          fontWeight="700"
          me="6px"
        >
          1,924
          <Text as="span" display={{ base: "none", md: "unset" }}>
            {" "}
            ETH
          </Text>
        </Text>
      </Flex>

      <SidebarResponsive
        routes={routes}
        isUserLayout={isUserLayout}
        setOpenSidebar={setOpenSidebar}
        openSidebar={openSidebar}
        openSubBar={openSubBar}
        setOpenSubBar={setOpenSubBar}
      />

      {/* <Menu>
        <MenuButton p="0px">
          <div className="notification-box">
            <Image
              className="notification-icon"
              src={notificationIcon}
              h="24px"
              w="24px"
            />
            <span className="count-noti">6</span>
          </div>
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="20px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
          mt="22px"
          me={{ base: "30px", md: "unset" }}
          minW={{ base: "unset", md: "400px", xl: "450px" }}
          maxW={{ base: "360px", md: "unset" }}
        >
          <Flex jusitfy="space-between" w="100%" mb="20px">
            <Text fontSize="md" fontWeight="600" color={textColor}>
              Notifications
            </Text>
            <Text
              fontSize="sm"
              fontWeight="500"
              color={textColorBrand}
              ms="auto"
              cursor="pointer"
            >
              Mark all read
            </Text>
          </Flex>
          <Flex flexDirection="column">
            <MenuItem
              _hover={{ bg: "none" }}
              _focus={{ bg: "none" }}
              px="0"
              borderRadius="8px"
              mb="10px"
            >
              <ItemContent info="Horizon UI Dashboard PRO" aName="Alicia" />
            </MenuItem>
            <MenuItem
              _hover={{ bg: "none" }}
              _focus={{ bg: "none" }}
              px="0"
              borderRadius="8px"
              mb="10px"
            >
              <ItemContent
                info="Horizon Design System Free"
                aName="Josh Henry"
              />
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu> */}
      {/* <FixedPlugin /> */}
      {/* <ThemeEditor navbarIcon={navbarIcon} /> */}

      {/* Role Switcher  */}
      {userRoles.length >= 2 && (
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={isSwitchingRole ? <Spinner size="xs" /> : <ChevronDownIcon />}
            variant="ghost"
            size="sm"
            mr={{ base: 1, md: 3 }}
            px={{ base: 2, md: 3 }}
            isLoading={isSwitchingRole}
            _hover={{ bg: "gray.100" }}
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="500"
            minW="auto"
          >
            <Text whiteSpace="nowrap">View As</Text>
          </MenuButton>
          <MenuList
            boxShadow={shadow}
            p="10px"
            borderRadius="12px"
            bg={menuBg}
            border="none"
            minW="180px"
          >
            {userRoles.map((role) => {
              const roleId = role.id;
              const currentActiveRoleId =
                currentActiveRole?._id?.toString() ||
                currentActiveRole?.toString() ||
                currentActiveRole;
              const roleIdString = roleId?.toString();
              const isActive = roleIdString === currentActiveRoleId;

              return (
                <MenuItem
                  key={roleId}
                  onClick={() => handleSwitchRole(roleId)}
                  isDisabled={isActive || isSwitchingRole}
                  _hover={{ bg: isActive ? "gray.100" : "gray.50" }}
                  _focus={{ bg: "none" }}
                  bg={isActive ? "gray.100" : "transparent"}
                  borderRadius="8px"
                  px="12px"
                  py="8px"
                >
                  <Flex alignItems="center" justifyContent="space-between" w="100%">
                    <Text fontSize="sm">{multipleRoleFormatter(role.name)}</Text>
                    {isActive && (
                      <Badge colorScheme="green" fontSize="xs">
                        Active
                      </Badge>
                    )}
                  </Flex>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      )}

      <Menu style={{ zIndex: 1500 }}>
        <MenuButton className="profileBtn">
          <Flex gap={{ base: "4px", md: "8px" }} alignItems={"center"} flexWrap="nowrap">
            <div className="user-img">
              <Image src={userIcon} height={{ base: "32px", md: "40px" }} width={{ base: "32px", md: "40px" }} />
            </div>
            <Flex
              className="user-info"
              flexDirection="column"
              display={{ base: "none", md: "flex" }}
              alignItems="flex-start"
            >
              <Text
                as="h5"
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="600"
                whiteSpace="nowrap"
              >
                {user}
              </Text>
              <Text
                as="h6"
                fontSize={{ base: "10px", md: "xs" }}
                color="gray.500"
                whiteSpace="nowrap"
              >
                {displayRoleName}
              </Text>
            </Flex>
          </Flex>
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="0px"
          mt="10px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
        >
          <Flex w="100%" mb="0px">
            <Text
              ps="20px"
              pt="16px"
              pb="10px"
              pr="16px"
              w="100%"
              borderBottom="1px solid"
              borderColor={borderColor}
              fontSize="sm"
              fontWeight="700"
              textTransform={"capitalize"}
              color={textColor}
            >
              👋&nbsp; Hey, {user}
            </Text>
          </Flex>

          <Flex flexDirection="column" p="10px">
            <MenuItem
              _hover={{ bg: "none" }}
              onClick={() => setIsOpen(true)}
              _focus={{ bg: "none" }}
              color="red.400"
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm">Log out</Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
      <LogoutModal
        handleLogout={logOut}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
      />
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
